import { Check, X } from 'lucide-react';
import { useEffect, useId, useMemo, useState } from 'react';

import { Input } from '@/components/primitives/Input';
import { Spinner } from '@/components/primitives/Loader';
import { useTranslation } from '@/hooks/useTranslation';
import { cn } from '@/lib/utils';
import { signupApi } from '@/modules/auth/services/signupApi';

import { MIN_PASSWORD_LENGTH, passwordRules, passwordStrength } from './passwordRules';

import type { PasswordRule } from './passwordRules';
import type { InputSize } from '@/components/primitives/Input';
import type { PasswordCheck } from '@/modules/auth/services/signupApi';

const CHECK_DEBOUNCE_MS = 500;

type RuleState = 'idle' | 'pending' | 'passed' | 'failed';

interface PasswordCheckState {
  generation: symbol;
  status: 'pending' | 'complete' | 'failed';
  result?: PasswordCheck;
}

const STRENGTH_COLOURS = [
  'bg-status-danger',
  'bg-status-danger',
  'bg-status-warning',
  'bg-status-success',
  'bg-status-success',
] as const;

export interface PasswordFieldProps {
  value: string;
  onChange: (value: string) => void;
  email?: string;
  firstName?: string;
  lastName?: string;
  error?: string;
  onValidityChange?: (valid: boolean) => void;
  label?: string;
  size?: InputSize;
}

export function PasswordField({
  value,
  onChange,
  email = '',
  firstName = '',
  lastName = '',
  error,
  onValidityChange,
  label,
  size = 'm',
}: PasswordFieldProps) {
  const { t } = useTranslation();
  const ruleLabels: Record<PasswordRule['id'], string> = {
    length: t('At least {count} characters', { count: MIN_PASSWORD_LENGTH }),
    'not-numeric': t('Not only numbers'),
    'not-personal': t('Not like your name or email'),
  };
  const [revealed, setRevealed] = useState(false);
  // Shown from the first focus and kept: folding them away on blur moved the form under a click.
  const [showRules, setShowRules] = useState(false);
  const [serverCheck, setServerCheck] = useState<PasswordCheckState | null>(null);
  // Invalidate verdicts during render, including A -> B -> A edits, without storing credentials.
  // Only the password is sent: the local rule covers the name and email, and the backend
  // turns down a half-typed email.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const generation = useMemo(() => Symbol(), [value]);

  const rules = passwordRules(value, email, firstName, lastName);
  const localValid = rules.every((rule) => rule.passed);
  // Whether the backend is asked depends on the password alone, so a name or email edit
  // that flips "Not like your name or email" doesn't recheck an unchanged password.
  const checkable = passwordRules(value).every((rule) => rule.passed);
  const serverErrorId = useId();
  const strength = passwordStrength(value);

  useEffect(() => {
    setServerCheck(null);
    if (!checkable) return;

    const controller = new AbortController();
    // Keep this advisory request out of both React Query caches: its payload contains credentials.
    const timer = setTimeout(async () => {
      setServerCheck({ generation, status: 'pending' });
      try {
        const result = await signupApi.checkPassword(value, controller.signal);
        if (!controller.signal.aborted) {
          setServerCheck({ generation, status: 'complete', result });
        }
      } catch {
        if (!controller.signal.aborted) {
          setServerCheck({ generation, status: 'failed' });
        }
      }
    }, CHECK_DEBOUNCE_MS);

    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [value, checkable, generation]);

  const currentCheck = serverCheck?.generation === generation ? serverCheck : null;
  const pending = currentCheck?.status === 'pending';
  const serverErrors = currentCheck?.result?.valid === false ? currentCheck.result.errors : [];
  // Waits for the backend's yes, so Sign up can't beat the check. If the check can't be
  // reached, the local rules stand in and the password is checked again on sign up.
  const valid =
    localValid && (currentCheck?.result?.valid === true || currentCheck?.status === 'failed');
  let backendRule: RuleState = 'idle';
  if (pending) backendRule = 'pending';
  else if (currentCheck?.result?.valid) backendRule = 'passed';
  else if (serverErrors.length > 0) backendRule = 'failed';

  useEffect(() => {
    onValidityChange?.(valid);
  }, [valid, onValidityChange]);

  return (
    <div>
      <div className="relative">
        <Input
          type={revealed ? 'text' : 'password'}
          size={size}
          label={label ?? t('Password')}
          required
          autoComplete="new-password"
          value={value}
          onChange={(event) => onChange(event.target.value)}
          onFocus={() => setShowRules(true)}
          // The backend's reasons show on its checklist line, so they are said once.
          error={error}
          aria-invalid={backendRule === 'failed' || undefined}
          // The Input links its own error; the backend's reason is linked only when that's absent.
          aria-describedby={backendRule === 'failed' && !error ? serverErrorId : undefined}
          // In the suffix rather than `trailingIcon`, which Input marks aria-hidden.
          suffix={
            <button
              type="button"
              onClick={() => setRevealed((shown) => !shown)}
              aria-label={revealed ? t('Hide password') : t('Show password')}
              aria-pressed={revealed}
              // Fills the suffix, which clips overflow, so the focus ring is inset.
              className={cn(
                'h-full font-medium transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand',
                size === 'l' ? '-mx-lg px-lg' : '-mx-md px-md'
              )}
            >
              {revealed ? t('Hide') : t('Show')}
            </button>
          }
        />

        {/* Drawn along the box's foot, which an error message would push it below. */}
        <div
          aria-hidden="true"
          className={cn(
            'pointer-events-none absolute inset-x-0 bottom-0 h-[3px] overflow-hidden bg-transparent',
            error && 'hidden'
          )}
        >
          <div
            className={cn(
              'h-full w-full origin-left transition-transform duration-300 ease-out',
              STRENGTH_COLOURS[strength]
            )}
            style={{ transform: `scaleX(${value.length === 0 ? 0 : (strength + 1) / 5})` }}
          />
        </div>
      </div>

      {currentCheck?.status === 'failed' && (
        <p role="status" className="mt-xs text-sm text-foreground-muted">
          {t(
            'Password check unavailable. You can still continue; your password will be checked when you sign up.'
          )}
        </p>
      )}

      <div
        className={cn(
          'grid transition-[grid-template-rows,opacity] duration-200 ease-out',
          showRules ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
        )}
      >
        <div className="overflow-hidden">
          <ul
            aria-live="polite"
            className="grid grid-cols-2 gap-x-lg gap-y-xs pt-sm text-sm text-foreground"
          >
            {rules.map((rule) => (
              <RuleItem
                key={rule.id}
                state={rule.passed ? 'passed' : 'idle'}
                label={ruleLabels[rule.id]}
              />
            ))}
            {/* Only the backend can say this one, so it waits on the backend's check. */}
            <RuleItem state={backendRule} label={t('Not a common password')} />
          </ul>
          {/* The backend's own words for why, under the line it crossed; full width, as they run long. */}
          {backendRule === 'failed' && (
            <p id={serverErrorId} role="alert" className="pt-xs text-sm text-status-danger">
              {serverErrors.join(' ')}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

/** One line of the password checklist: empty, ticked, crossed, or waiting on the backend. */
function RuleItem({ state, label }: { state: RuleState; label: string }) {
  return (
    <li className={cn('flex items-start gap-xs', state === 'failed' && 'text-status-danger')}>
      {state === 'pending' ? (
        <span data-testid="password-check-pending" className="mt-[3px] flex size-4 shrink-0">
          <Spinner size="m" inline active />
        </span>
      ) : (
        <span
          aria-hidden="true"
          className={cn(
            'mt-[3px] flex size-4 shrink-0 items-center justify-center rounded-full border transition-colors',
            state === 'passed' &&
              'border-status-success bg-status-success text-foreground-inverted',
            state === 'failed' && 'border-status-danger bg-status-danger text-foreground-inverted',
            state === 'idle' && 'border-outline-input'
          )}
        >
          {state === 'passed' && <Check className="size-3" strokeWidth={3} />}
          {state === 'failed' && <X className="size-3" strokeWidth={3} />}
        </span>
      )}
      <span>{label}</span>
    </li>
  );
}
